// sampleData.js — the "instant demo classroom" engine.
//
// Why this exists: teachers bounce in seconds if a new account lands in an empty
// app (no class, no students, every screen blank). The moment someone registers we
// seed a FULLY-ALIVE sample classroom — roster, sign-out history, today's
// attendance, kids currently out, a library + a lending tracker — so they can click
// around every feature with realistic data, exactly like ClassDojo's demo class.
//
// Everything written here is flagged `isSample: true` AND tracked in a `sampleSeed`
// manifest on the user doc, so `removeSampleClassroom` is an exact, reversible
// factory-reset (it even undoes the user-doc library/tracker changes the seed made).
//
// Needs ZERO security-rules changes: a brand-new teacher already owns their
// provisional "school of one", so they may write their own classes (teacherId==uid),
// students (addedBy==uid), sign-outs (teacherUID==uid), sign-out history
// (users/{uid}/signedInHistory, owner), attendance (owner) and library/lending
// records (owner). Mirrors the exact doc shapes the real screens write, so the
// sample is indistinguishable from real data everywhere it's read.

import {
  doc, collection, getDoc, getDocs, writeBatch, serverTimestamp, deleteField,
} from "firebase/firestore";
import { firestore } from "./FirebaseConfig";
import { gradeFields } from "./gradeModel";

export const SAMPLE_VERSION = 1;
// Stable id so the factory-reset can pull exactly this tracker back out of the
// user's lendingTrackers[] without touching any real ones they may have added.
const SAMPLE_TRACKER_ID = "sample-chromebooks";

// ── Local date helpers (match useFilteredStudents.todayKeyLocal) ───────────────
function todayKeyLocal(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
// A timestamp `daysAgo` days back at h:m local — used to scatter realistic history.
function at(daysAgo, h, m) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}
function plusMins(iso, mins) {
  return new Date(new Date(iso).getTime() + mins * 60000).toISOString();
}

// ── The sample roster ──────────────────────────────────────────────────────────
const SAMPLE_STUDENTS = [
  { firstName: "Ava",     lastName: "Thompson", studentId: "1001" },
  { firstName: "Liam",    lastName: "Garcia",   studentId: "1002" },
  { firstName: "Maya",    lastName: "Patel",    studentId: "1003" },
  { firstName: "Noah",    lastName: "Williams", studentId: "1004" },
  { firstName: "Sofia",   lastName: "Nguyen",   studentId: "1005" },
  { firstName: "Ethan",   lastName: "Brooks",   studentId: "1006" },
  { firstName: "Olivia",  lastName: "Rivera",   studentId: "1007" },
  { firstName: "Jackson", lastName: "Lee",      studentId: "1008" },
];
const SAMPLE_GRADE = "3"; // canonical (gradeModel); shows as 3rd grade

// A few well-known classroom books (real ISBNs) for the library catalog.
const SAMPLE_BOOKS = [
  { isbn: "9780064430173", title: "Frog and Toad Are Friends", author: "Arnold Lobel",   publishedDate: "1970", genre: "Early Reader" },
  { isbn: "9780545158800", title: "The One and Only Ivan",     author: "Katherine Applegate", publishedDate: "2012", genre: "Fiction" },
  { isbn: "9780823447763", title: "Last Stop on Market Street", author: "Matt de la Peña", publishedDate: "2015", genre: "Picture Book" },
];

// Build the ~12 sign-out history trips (so Insights + every chart populate). Ethan
// is the deliberate "frequent flyer" so the patterns charts have something to show.
function buildHistoryTrips() {
  // [studentIndex, daysAgo, hour, min, durationMins, location]
  return [
    [5, 0, 9, 15, 7,  "Bathroom"],
    [5, 0, 11, 5, 12, "Water Fountain"],
    [5, 1, 10, 30, 9, "Bathroom"],
    [5, 2, 13, 20, 18, "Nurse"],
    [5, 3, 9, 45, 6,  "Bathroom"],
    [0, 1, 10, 10, 5, "Bathroom"],
    [0, 4, 12, 0, 8,  "Library"],
    [2, 2, 11, 30, 4, "Water Fountain"],
    [3, 5, 13, 15, 22, "Office"],
    [1, 3, 9, 50, 6,  "Bathroom"],
    [4, 6, 10, 40, 7, "Library"],
    [6, 7, 14, 5, 5,  "Bathroom"],
  ];
}

/**
 * Seed a brand-new teacher's demo classroom. Idempotent: a no-op if a sampleSeed
 * manifest already exists. Best-effort by design — callers wrap in try/catch so a
 * seeding hiccup never blocks sign-up.
 */
export async function seedSampleClassroom(uid, schoolId, teacherName, db = firestore) {
  if (!uid || !schoolId) return false;

  // Guard against double-seeding (e.g. a retried registration).
  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists() && userSnap.data().sampleSeed) return false;

  const name = teacherName || "Teacher";
  const today = new Date();
  const schoolYear = `${today.getFullYear()}-${today.getFullYear() + 1}`;

  const batch = writeBatch(db);

  // 1) Pre-generate refs so we can cross-link class ↔ students in one pass.
  const classRef = doc(collection(db, "classes"));
  const studentRefs = SAMPLE_STUDENTS.map(() => doc(collection(db, "students")));
  const studentDocIds = studentRefs.map((r) => r.id);

  // 2) Students
  SAMPLE_STUDENTS.forEach((s, i) => {
    batch.set(studentRefs[i], {
      firstName: s.firstName,
      lastName: s.lastName,
      studentName: `${s.firstName} ${s.lastName}`,
      studentId: s.studentId,
      ...gradeFields(SAMPLE_GRADE),
      schoolEmail: "",
      enrolledClasses: [classRef.id],
      schoolId,
      addedBy: uid,
      teachers: [uid],
      shared: false,
      isSample: true,
    });
  });

  // 3) The class (homeroom so it shows on attendance + emergency by default)
  batch.set(classRef, {
    className: "✨ Sample Class",
    gradeLevel: "3rd",
    schoolYear,
    roomNumber: "101",
    teacherId: uid,
    schoolId,
    isHomeroom: true,
    isSample: true,
    enrolledStudents: studentDocIds,
  });

  // 4) Sign-out history → users/{uid}/signedInHistory (powers Insights + charts)
  buildHistoryTrips().forEach(([si, daysAgo, h, m, dur, location]) => {
    const s = SAMPLE_STUDENTS[si];
    const timeOut = at(daysAgo, h, m);
    const histRef = doc(collection(db, "users", uid, "signedInHistory"));
    batch.set(histRef, {
      studentDocumentNumber: studentDocIds[si],
      studentName: `${s.firstName} ${s.lastName}`,
      idNumber: s.studentId,
      grade: SAMPLE_GRADE,
      schoolId,
      teacherUID: uid,
      teacherName: name,
      classId: classRef.id,
      className: "✨ Sample Class",
      room: "101",
      location,
      time_out: timeOut,
      time_in: plusMins(timeOut, dur),
      isSample: true,
    });
  });

  // 5) Two kids currently OUT → top-level signouts (live grid shows them)
  const outNow = [
    { si: 1, location: "Bathroom",       agoMin: 4 },
    { si: 5, location: "Water Fountain", agoMin: 12 },
  ];
  const signoutIds = [];
  outNow.forEach(({ si, location, agoMin }) => {
    const s = SAMPLE_STUDENTS[si];
    const ref = doc(collection(db, "signouts"));
    signoutIds.push(ref.id);
    batch.set(ref, {
      studentDocumentNumber: studentDocIds[si],
      studentName: `${s.firstName} ${s.lastName}`,
      idNumber: s.studentId,
      grade: SAMPLE_GRADE,
      schoolId,
      teacherUID: uid,
      teacherName: name,
      classId: classRef.id,
      className: "✨ Sample Class",
      homeroom: "✨ Sample Class",
      room: "101",
      location,
      time_out: new Date(Date.now() - agoMin * 60000).toISOString(),
      time_in: null,
      isSample: true,
    });
  });

  // 7) Library: enable it, seed the catalog, check one book out
  SAMPLE_BOOKS.forEach((b) => {
    batch.set(doc(db, "users", uid, "libraryCatalog", b.isbn), {
      ...b,
      description: "",
      thumbnail: "",
      pageCount: null,
      addedAt: serverTimestamp(),
      isSample: true,
    });
  });
  const checkedOutBook = SAMPLE_BOOKS[0];
  const borrower = SAMPLE_STUDENTS[4]; // Sofia
  batch.set(doc(collection(db, "classes", classRef.id, "lendingRecords")), {
    trackerId: "library",
    trackerName: "Library",
    studentDocumentNumber: studentDocIds[4],
    studentName: `${borrower.firstName} ${borrower.lastName}`,
    studentId: borrower.studentId,
    isbn: checkedOutBook.isbn,
    bookTitle: checkedOutBook.title,
    bookAuthor: checkedOutBook.author,
    fieldValues: { Title: checkedOutBook.title, Author: checkedOutBook.author },
    time_out: at(2, 9, 30),
    time_in: null,
    teacherUID: uid,
    classId: classRef.id,
    className: "✨ Sample Class",
    isSample: true,
  });

  // 8) Lending: a Chromebooks tracker + one loan out
  const sampleTracker = {
    id: SAMPLE_TRACKER_ID,
    name: "Chromebooks",
    color: "#59738d",
    overdueDays: 1,
    fields: [{ label: "Asset Tag" }],
  };
  const cbBorrower = SAMPLE_STUDENTS[3]; // Noah
  batch.set(doc(collection(db, "classes", classRef.id, "lendingRecords")), {
    trackerId: SAMPLE_TRACKER_ID,
    trackerName: "Chromebooks",
    studentDocumentNumber: studentDocIds[3],
    studentName: `${cbBorrower.firstName} ${cbBorrower.lastName}`,
    studentId: cbBorrower.studentId,
    fieldValues: { "Asset Tag": "CB-042" },
    time_out: new Date(Date.now() - 3 * 60 * 60000).toISOString(),
    time_in: null,
    teacherUID: uid,
    classId: classRef.id,
    className: "✨ Sample Class",
    isSample: true,
  });

  // 9) The manifest + the user-doc changes the seed made (library on, tracker added)
  batch.set(
    userRef,
    {
      libraryEnabled: true,
      lendingTrackers: [...(userSnap.data()?.lendingTrackers || []), sampleTracker],
      sampleSeed: {
        version: SAMPLE_VERSION,
        seededAt: new Date().toISOString(),
        classId: classRef.id,
        studentDocIds,
        signoutIds,
        bookIsbns: SAMPLE_BOOKS.map((b) => b.isbn),
        addedTrackerId: SAMPLE_TRACKER_ID,
        setLibraryEnabled: true,
      },
    },
    { merge: true }
  );

  await batch.commit();

  // Today's attendance must be a SECOND batch: the attendance rule (canAccessClass)
  // does a get() on the class doc, which isn't visible until the class above is
  // actually committed — writing it in the same batch would be denied. First 6
  // present, last 2 left absent so the school-wide absent board has something to show.
  const dateKey = todayKeyLocal(today);
  const attBatch = writeBatch(db);
  studentDocIds.slice(0, 6).forEach((sid) => {
    attBatch.set(
      doc(db, "classes", classRef.id, "attendance", dateKey, "students", sid),
      { timestamp: serverTimestamp() }
    );
  });
  await attBatch.commit();

  return true;
}

/**
 * Trim the seeded sample to match the tools a teacher chose in the onboarding
 * wizard. The sample is seeded FULL at registration (so an abandoned wizard still
 * lands in a live app); when the wizard finishes we prune the parts they turned
 * off — Michael's "no Library section if they turned Library off". Safe no-op if
 * there's no sample (real accounts) or nothing to trim.
 *
 * `features` = { library, lending } booleans (other keys ignored here).
 */
export async function pruneSampleToFeatures(uid, features = {}, db = firestore) {
  if (!uid) return false;
  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);
  const seed = userSnap.exists() ? userSnap.data().sampleSeed : null;
  if (!seed) return false;

  const ops = [];
  const userUpdate = {};

  // Library off → drop the catalog books + the sample library checkout, and undo
  // the libraryEnabled the seed set (only if the seed is what turned it on).
  if (features.library === false) {
    (seed.bookIsbns || []).forEach((isbn) =>
      ops.push((b) => b.delete(doc(db, "users", uid, "libraryCatalog", isbn)))
    );
    if (seed.classId) {
      const loans = await getDocs(collection(doc(db, "classes", seed.classId), "lendingRecords")); // eslint-disable-line no-await-in-loop
      loans.docs
        .filter((d) => d.data().trackerId === "library")
        .forEach((d) => ops.push((b) => b.delete(d.ref)));
    }
    if (seed.setLibraryEnabled) userUpdate.libraryEnabled = false;
  }

  // Lending off → drop the Chromebooks tracker + its sample loan.
  if (features.lending === false && seed.addedTrackerId) {
    if (seed.classId) {
      const loans = await getDocs(collection(doc(db, "classes", seed.classId), "lendingRecords")); // eslint-disable-line no-await-in-loop
      loans.docs
        .filter((d) => d.data().trackerId === seed.addedTrackerId)
        .forEach((d) => ops.push((b) => b.delete(d.ref)));
    }
    const trackers = (userSnap.data().lendingTrackers || []).filter(
      (t) => t.id !== seed.addedTrackerId
    );
    userUpdate.lendingTrackers = trackers;
  }

  if (ops.length) await commitInChunks(ops, db);
  if (Object.keys(userUpdate).length) {
    await writeBatch(db).set(userRef, userUpdate, { merge: true }).commit();
  }
  return true;
}

// Commit a list of write-ops in ≤450-op chunks (Firestore's hard limit is 500).
async function commitInChunks(ops, db) {
  for (let i = 0; i < ops.length; i += 450) {
    const batch = writeBatch(db);
    ops.slice(i, i + 450).forEach((fn) => fn(batch));
    await batch.commit(); // eslint-disable-line no-await-in-loop
  }
}

/**
 * Factory-reset: remove EVERYTHING the seed added and undo its user-doc changes.
 * Reads the sampleSeed manifest, enumerates the class's subcollections (the client
 * can't recursively delete), then deletes + reverts in batched writes.
 * Returns false if there's nothing to remove.
 */
export async function removeSampleClassroom(uid, db = firestore) {
  if (!uid) return false;
  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);
  const seed = userSnap.exists() ? userSnap.data().sampleSeed : null;
  if (!seed) return false;

  const ops = [];

  // Enumerate the sample class's subcollections (lendingRecords + attendance).
  if (seed.classId) {
    const classPath = doc(db, "classes", seed.classId);

    const loanSnap = await getDocs(collection(classPath, "lendingRecords"));
    loanSnap.docs.forEach((d) => ops.push((b) => b.delete(d.ref)));

    const attDays = await getDocs(collection(classPath, "attendance"));
    for (const day of attDays.docs) {
      const studs = await getDocs(collection(day.ref, "students")); // eslint-disable-line no-await-in-loop
      studs.docs.forEach((d) => ops.push((b) => b.delete(d.ref)));
      ops.push((b) => b.delete(day.ref));
    }
    ops.push((b) => b.delete(classPath));
  }

  // Students, active sign-outs, library books — all by tracked id.
  (seed.studentDocIds || []).forEach((id) =>
    ops.push((b) => b.delete(doc(db, "students", id)))
  );
  (seed.signoutIds || []).forEach((id) =>
    ops.push((b) => b.delete(doc(db, "signouts", id)))
  );
  (seed.bookIsbns || []).forEach((isbn) =>
    ops.push((b) => b.delete(doc(db, "users", uid, "libraryCatalog", isbn)))
  );

  // Sign-out history is many docs with no stored ids → query by the isSample flag.
  const histSnap = await getDocs(collection(db, "users", uid, "signedInHistory"));
  histSnap.docs
    .filter((d) => d.data().isSample === true)
    .forEach((d) => ops.push((b) => b.delete(d.ref)));

  await commitInChunks(ops, db);

  // Finally, revert the user-doc changes the seed made + drop the manifest.
  const trackers = (userSnap.data().lendingTrackers || []).filter(
    (t) => t.id !== seed.addedTrackerId
  );
  const revert = writeBatch(db);
  const userUpdate = { lendingTrackers: trackers, sampleSeed: deleteField() };
  if (seed.setLibraryEnabled) userUpdate.libraryEnabled = false;
  revert.set(userRef, userUpdate, { merge: true });
  await revert.commit();

  return true;
}
